"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import Image from "next/image";
import { X, Send, AlertCircle, Wrench, Loader2 } from "lucide-react";
import CaonaboVoiceButtonMaybe from "./CaonaboVoiceButtonMaybe";
import CaonaboTimeline, { type TimelineStep } from "./CaonaboTimeline";
import ChatRichContent from "./rich/ChatRichContent";
import { dedupRichMaps, type RichContent } from "@/lib/caonabo/rich-content";

type ChatMessage =
  | { rol: "user"; contenido: string }
  | { rol: "assistant"; contenido: string; richContent?: RichContent[] }
  | { rol: "tool"; contenido: string; toolName?: string | null };

const SESSION_KEY = "caonabo_session_id";

function getOrCreateSessionId(): string {
  if (typeof window === "undefined") return "";
  let id = sessionStorage.getItem(SESSION_KEY);
  if (!id) {
    id =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    sessionStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

export default function CaonaboWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Init session
  useEffect(() => {
    setSessionId(getOrCreateSessionId());
  }, []);

  // Hidratar historial al abrir por primera vez
  useEffect(() => {
    if (!open || !sessionId || hydrated) return;
    fetch(`/api/caonabo/chat?sessionId=${encodeURIComponent(sessionId)}`, {
      cache: "no-store",
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data?.ok && Array.isArray(data.messages)) {
          // Filtros de hidratación:
          // - Saltar assistant con contenido vacío (solo tool_use markers)
          // - Para tool: dejar contenido como nombre + duración (no JSON crudo)
          const hydrated: ChatMessage[] = [];
          for (const m of data.messages as Array<{
            rol: string;
            contenido: string;
            toolName?: string | null;
          }>) {
            if (m.rol === "assistant" && (!m.contenido || m.contenido.trim() === "")) {
              continue; // marcador de tool_use, lo omitimos del UI
            }
            if (m.rol === "tool") {
              // Las tool calls se ven en el timeline durante streaming. En
              // historial las omitimos para no saturar — el resumen del
              // assistant ya cuenta la historia.
              continue;
            }
            hydrated.push({
              rol: m.rol as ChatMessage["rol"],
              contenido: m.contenido,
              toolName: m.toolName,
            });
          }
          setMessages(hydrated);
        }
        setHydrated(true);
      })
      .catch(() => setHydrated(true));
  }, [open, sessionId, hydrated]);

  // Auto-scroll abajo al mensaje nuevo
  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  // Focus input al abrir
  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  const [statusText, setStatusText] = useState<string | null>(null);
  const [streamingText, setStreamingText] = useState("");
  const [streamingRich, setStreamingRich] = useState<RichContent[]>([]);
  const [timeline, setTimeline] = useState<TimelineStep[]>([]);
  const stepCounterRef = useRef(0);

  // Confirmación 2-step: cuando el usuario hace click en "Sí" en una
  // ChatConfirmCard, mandamos al LLM una instrucción literal explícita
  // que le indica que ejecute la última tool propuesta con _confirmed=true,
  // sin volver a pedir confirmación. Display al usuario: "Confirmo".
  const handleConfirmTool = useCallback(
    (toolName: string, _pendingInput: Record<string, unknown>) => {
      const instruccion =
        `[CONFIRMACIÓN_USUARIO=SI] Confirmo la operación. ` +
        `INSTRUCCIÓN: ejecuta INMEDIATAMENTE la tool ${toolName} con ` +
        `EXACTAMENTE los mismos parámetros que propusiste antes + _confirmed: true. ` +
        `NO me vuelvas a pedir confirmación. Solo ejecuta y reporta el resultado.`;
      send(instruccion, "✓ Confirmo");
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const handleCancelTool = useCallback(() => {
    send(
      "[CONFIRMACIÓN_USUARIO=NO] Cancelo la operación propuesta. No la ejecutes.",
      "✗ Cancelar"
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Warmup del cache al abrir el widget por primera vez (silencioso)
  const warmedUpRef = useRef(false);
  useEffect(() => {
    if (open && !warmedUpRef.current) {
      warmedUpRef.current = true;
      // Llamada silenciosa a /status para precalentar conexión + Prisma
      fetch("/api/caonabo/status", { cache: "no-store" }).catch(() => {});
    }
  }, [open]);

  // Auto-scroll robusto con ResizeObserver: se mantiene pegado al fondo
  // mientras llega contenido nuevo (streaming token-por-token).
  useEffect(() => {
    if (!scrollRef.current) return;
    const el = scrollRef.current;
    const ro = new ResizeObserver(() => {
      el.scrollTop = el.scrollHeight;
    });
    ro.observe(el);
    // Observar también children dinámicos
    for (const child of Array.from(el.children)) {
      ro.observe(child);
    }
    return () => ro.disconnect();
  }, [open]);

  const send = useCallback(async (overrideText?: string, displayText?: string) => {
    const text = (overrideText ?? input).trim();
    if (!text || loading || !sessionId) return;
    if (!overrideText) setInput("");
    setError(null);
    setStatusText("Procesando…");
    setStreamingText("");
    setStreamingRich([]);
    setTimeline([]);
    stepCounterRef.current = 0;
    // displayText muestra al usuario una versión amigable (ej: "Confirmo")
    // mientras el LLM recibe la instrucción literal completa.
    setMessages((prev) => [
      ...prev,
      { rol: "user", contenido: displayText ?? text },
    ]);
    setLoading(true);
    try {
      const r = await fetch("/api/caonabo/chat/stream", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionId, message: text }),
      });
      // Si el server respondió con error JSON (capa OFF, etc.), parsear
      const ct = r.headers.get("content-type") ?? "";
      if (!r.ok || !ct.includes("text/event-stream")) {
        const data = await r.json().catch(() => ({}));
        const reason = data?.reason ?? `HTTP ${r.status}`;
        if (reason === "chat_text_disabled") {
          setError("Chat está desactivado en /admin/configuracion-ai.");
        } else if (reason === "unauthenticated") {
          setError("Inicia sesión para usar Caonabo.");
        } else {
          setError(data?.message || `Error: ${reason}`);
        }
        return;
      }

      // Leer stream SSE
      const reader = r.body!.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let accumulated = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop() ?? "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const json = line.slice(6).trim();
          if (!json) continue;
          let ev:
            | { type: "delta"; text: string }
            | { type: "status"; phase: string; detail?: string }
            | { type: "tool_call"; name: string; durationMs: number; ok: boolean }
            | { type: "rich_content"; content: RichContent }
            | { type: "done"; reply: string; richContent?: RichContent[] }
            | { type: "error"; message: string };
          try {
            ev = JSON.parse(json);
          } catch {
            continue;
          }
          if (ev.type === "delta") {
            accumulated += ev.text;
            setStreamingText(accumulated);
          } else if (ev.type === "status") {
            const labels: Record<string, string> = {
              thinking: "Pensando…",
              tool_start: "Ejecutando herramientas…",
              tool_end: "Procesando resultado…",
              compose: "Componiendo respuesta…",
            };
            setStatusText(labels[ev.phase] ?? ev.phase);
            // Si tool_start trae detail (CSV de tool names), generar steps
            if (ev.phase === "tool_start" && ev.detail) {
              const tools = ev.detail.split(",").map((t) => t.trim()).filter(Boolean);
              const newSteps: TimelineStep[] = tools.map((tool) => ({
                stepIndex: stepCounterRef.current++,
                tool,
                status: "running",
                startedAt: Date.now(),
              }));
              setTimeline((prev) => [...prev, ...newSteps]);
            }
          } else if (ev.type === "tool_call") {
            // Actualizar el step running más reciente con este nombre
            setTimeline((prev) => {
              // Buscar el último step running con ese tool y marcar como done
              const next = [...prev];
              for (let i = next.length - 1; i >= 0; i--) {
                if (next[i].tool === ev.name && next[i].status === "running") {
                  next[i] = {
                    ...next[i],
                    status: ev.ok ? "done" : "error",
                    durationMs: ev.durationMs,
                  };
                  return next;
                }
              }
              // Si no había step running (ej: tool_start no llegó), crear uno done
              return [
                ...next,
                {
                  stepIndex: stepCounterRef.current++,
                  tool: ev.name,
                  status: ev.ok ? "done" : "error",
                  startedAt: Date.now() - ev.durationMs,
                  durationMs: ev.durationMs,
                },
              ];
            });
          } else if (ev.type === "rich_content") {
            setStreamingRich((prev) => [...prev, ev.content]);
          } else if (ev.type === "done") {
            const finalRich = ev.richContent && ev.richContent.length > 0 ? ev.richContent : undefined;
            setMessages((prev) => [
              ...prev,
              {
                rol: "assistant",
                contenido: ev.reply,
                richContent: finalRich,
              },
            ]);
            setStreamingText("");
            setStreamingRich([]);
            setStatusText(null);
          } else if (ev.type === "error") {
            setError(ev.message);
            setStreamingText("");
            setStatusText(null);
          }
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error de red");
    } finally {
      setLoading(false);
      setStatusText(null);
    }
  }, [input, loading, sessionId]);

  // ── Voice → chat: convertir transcripciones en mensajes del hilo. ──
  // Usamos un ref para saber si la última respuesta assistant es la de voz
  // streaming (y le agregamos deltas) vs si es histórica (y creamos nueva).
  const voiceStreamingIdxRef = useRef<number | null>(null);

  const handleVoiceUserTranscript = useCallback((text: string) => {
    if (!text.trim()) return;
    setMessages((prev) => [...prev, { rol: "user", contenido: text }]);
    // Nuevo turno — limpiar timeline efímero (los tool calls del turno
    // anterior ya quedaron en messages como rich content adjunto).
    setTimeline([]);
    setStatusText(null);
    voiceStreamingIdxRef.current = null;
  }, []);

  const handleVoiceAssistantDelta = useCallback((delta: string) => {
    if (!delta) return;
    setMessages((prev) => {
      const idx = voiceStreamingIdxRef.current;
      if (idx !== null && prev[idx]?.rol === "assistant") {
        const next = prev.slice();
        const cur = next[idx] as { rol: "assistant"; contenido: string; richContent?: RichContent[] };
        next[idx] = { ...cur, contenido: cur.contenido + delta };
        return next;
      }
      const newIdx = prev.length;
      voiceStreamingIdxRef.current = newIdx;
      return [...prev, { rol: "assistant", contenido: delta }];
    });
  }, []);

  // Voice: thinking indicator. Usa el mismo state que el chat texto.
  const handleVoiceThinking = useCallback((active: boolean, detail?: string) => {
    setLoading(active);
    setStatusText(active ? (detail ?? "Pensando…") : null);
  }, []);

  // Voice: tool call → push al timeline (mismo state que el chat texto).
  const handleVoiceToolCall = useCallback(
    (info: { name: string; ok: boolean; durationMs: number }) => {
      setTimeline((prev) => [
        ...prev,
        {
          stepIndex: prev.length,
          tool: info.name,
          status: info.ok ? "done" : "error",
          startedAt: Date.now() - info.durationMs,
          durationMs: info.durationMs,
        },
      ]);
    },
    []
  );

  // Voice: rich content (tabla/detail/result/confirm) → adjuntar al último
  // mensaje assistant. Si todavía no hay assistant, lo creamos vacío para
  // que la tabla aparezca antes del texto de respuesta.
  const handleVoiceRichContent = useCallback((content: RichContent) => {
    setMessages((prev) => {
      const idx = voiceStreamingIdxRef.current;
      if (idx !== null && prev[idx]?.rol === "assistant") {
        const next = prev.slice();
        const cur = next[idx] as {
          rol: "assistant";
          contenido: string;
          richContent?: RichContent[];
        };
        next[idx] = {
          ...cur,
          richContent: [...(cur.richContent ?? []), content],
        };
        return next;
      }
      // No hay assistant streaming aún → crear uno con la rich content adjunta.
      const newIdx = prev.length;
      voiceStreamingIdxRef.current = newIdx;
      return [
        ...prev,
        { rol: "assistant", contenido: "", richContent: [content] },
      ];
    });
  }, []);

  // Lock body scroll mientras el modal está abierto (estilo Kuadre).
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);

  // Cerrar con tecla Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const handleVoiceAssistantDone = useCallback((text: string) => {
    setMessages((prev) => {
      const idx = voiceStreamingIdxRef.current;
      if (idx !== null && prev[idx]?.rol === "assistant") {
        const next = prev.slice();
        // PRESERVAR richContent que se adjuntó durante el streaming.
        // Si lo sobrescribimos sin spread, las tablas dinámicas desaparecen
        // cuando el bot termina de hablar.
        const cur = next[idx] as {
          rol: "assistant";
          contenido: string;
          richContent?: RichContent[];
        };
        next[idx] = {
          ...cur,
          contenido: text,
        };
        return next;
      }
      return [...prev, { rol: "assistant", contenido: text }];
    });
    voiceStreamingIdxRef.current = null;
  }, []);

  return (
    <>
      {/* Floating button con isotipo Cacique */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Abrir Caonabo IA"
          className="fixed bottom-20 right-4 z-50 inline-flex items-center justify-center rounded-full shadow-lg transition-transform hover:scale-105 ring-2"
          style={{
            width: 56,
            height: 56,
            background: "linear-gradient(135deg, #8B3A2E 0%, #6B2820 100%)",
            color: "white",
            boxShadow: "0 6px 20px rgba(139,58,46,0.45), 0 2px 6px rgba(0,0,0,0.3)",
          }}
        >
          <Image
            src="/images/isotipo.png"
            alt="Caonabo IA"
            width={32}
            height={32}
            priority
            style={{ filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.4))" }}
          />
        </button>
      )}

      {/* Modal estilo Kuadre: backdrop + container centrado / bottom-sheet mobile */}
      {open && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm caonabo-fade-in"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          {/* Container */}
          <div
            className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center sm:p-6 pointer-events-none"
            role="dialog"
            aria-modal="true"
            aria-label="Caonabo IA"
          >
            <div
              className="caonabo-slide-up relative w-full h-[92vh] sm:h-auto sm:max-h-[85vh] sm:max-w-[880px] bg-surface rounded-t-3xl sm:rounded-3xl overflow-hidden flex flex-col pointer-events-auto border"
              style={{
                borderColor: "rgba(139,58,46,0.30)",
                boxShadow:
                  "0 25px 60px rgba(0,0,0,0.55), 0 6px 16px rgba(139,58,46,0.30)",
              }}
            >
              {/* Bottom-sheet handle (solo mobile) */}
              <div className="sm:hidden flex justify-center pt-2 pb-1 flex-shrink-0">
                <div className="w-10 h-1 rounded-full bg-white/20" />
              </div>

              {/* Header */}
              <div
                className="relative flex items-center justify-between px-4 sm:px-6 py-3 sm:py-3.5 flex-shrink-0 border-b"
                style={{
                  borderBottomColor: "rgba(139,58,46,0.25)",
                  background:
                    "linear-gradient(135deg, rgba(139,58,46,0.14) 0%, rgba(139,58,46,0.04) 100%)",
                }}
              >
                {/* Línea gradient superior estilo Kuadre */}
                <div
                  className="hidden sm:block absolute top-0 left-0 right-0 h-[2px]"
                  style={{
                    background:
                      "linear-gradient(to right, #8B3A2E, #D77F70, #8B3A2E)",
                  }}
                />

                <div className="flex items-center gap-3 min-w-0">
                  {/* Avatar con halo + status dot */}
                  <div className="relative flex-shrink-0">
                    <div
                      className="absolute inset-0 rounded-2xl blur-md opacity-60"
                      style={{
                        background:
                          "linear-gradient(135deg, #8B3A2E, #D77F70, #8B3A2E)",
                      }}
                    />
                    <div
                      className="relative w-10 h-10 sm:w-11 sm:h-11 rounded-2xl flex items-center justify-center shadow-md"
                      style={{
                        background: "linear-gradient(135deg, #8B3A2E, #6B2820)",
                        boxShadow: "0 4px 12px rgba(139,58,46,0.4)",
                      }}
                    >
                      <Image
                        src="/images/isotipo.png"
                        alt=""
                        width={22}
                        height={22}
                        priority
                      />
                    </div>
                    {/* Status dot */}
                    <div
                      className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-surface ${
                        loading ? "bg-amber-400 animate-pulse" : "bg-emerald-400"
                      }`}
                    />
                  </div>

                  <div className="min-w-0">
                    <h2
                      className="text-base sm:text-lg font-semibold tracking-tight"
                      style={{ color: "#D77F70" }}
                    >
                      Caonabo IA
                    </h2>
                    <p className="text-[11px] sm:text-xs text-text-muted truncate">
                      {loading ? "Pensando…" : "Tu asistente del campo · beta"}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-1 sm:gap-1.5 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    aria-label="Cerrar"
                    className="p-2 rounded-xl hover:bg-white/5 transition-colors text-text-muted hover:text-text"
                  >
                    <X size={18} />
                  </button>
                </div>
              </div>

              {/* Messages */}
              <div
                ref={scrollRef}
                className="flex-1 overflow-y-auto px-3 sm:px-6 py-3 sm:py-4 space-y-2 min-h-0"
              >
            {messages.length === 0 && !loading && (
              <div className="text-xs text-text-muted text-center py-6 space-y-3">
                <div className="flex justify-center">
                  <span
                    className="inline-flex items-center justify-center rounded-2xl"
                    style={{
                      width: 56,
                      height: 56,
                      background:
                        "linear-gradient(135deg, rgba(139,58,46,0.15), rgba(139,58,46,0.05))",
                      border: "1px solid rgba(139,58,46,0.30)",
                    }}
                  >
                    <Image src="/images/isotipo.png" alt="" width={32} height={32} priority />
                  </span>
                </div>
                <div>
                  <p className="text-sm font-bold text-text">Soy Caonabo</p>
                  <p className="text-[11px]">Tu asistente del ERP agrícola</p>
                </div>
                <div className="text-[10px] text-text-muted/80 space-y-1 mt-3 px-2">
                  <p className="text-[9px] uppercase tracking-wider">Prueba preguntando:</p>
                  <p className="rounded-md bg-surface-hover/40 border border-border px-2 py-1">
                    "OAs vencidas esta semana"
                  </p>
                  <p className="rounded-md bg-surface-hover/40 border border-border px-2 py-1">
                    "productores intermediarios de Barahona"
                  </p>
                  <p className="rounded-md bg-surface-hover/40 border border-border px-2 py-1">
                    "qué es BRAM"
                  </p>
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <Message
                key={i}
                message={m}
                onConfirmTool={handleConfirmTool}
                onCancelTool={handleCancelTool}
              />
            ))}
            {/* Timeline: aparece cuando hay tool calls en progreso O cuando
                está pensando sin texto streaming aún */}
            {loading && (timeline.length > 0 || !streamingText) && (
              <CaonaboTimeline statusText={statusText} timeline={timeline} />
            )}
            {/* Streaming rich content (en vivo durante el agentic loop) */}
            {streamingRich.length > 0 && (
              <div className="space-y-2">
                {dedupRichMaps(streamingRich).map((rc, i) => (
                  <ChatRichContent
                    key={`stream-rc-${i}`}
                    content={rc}
                    onConfirm={handleConfirmTool}
                    onCancel={handleCancelTool}
                  />
                ))}
              </div>
            )}
            {/* Streaming text: cursor parpadeante terracotta */}
            {streamingText && (
              <div className="flex justify-start caonabo-fade-in">
                <div
                  className="max-w-[90%] text-xs px-3 py-2 rounded-2xl rounded-bl-sm whitespace-pre-wrap text-text"
                  style={{
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  {streamingText}
                  <span
                    className="caonabo-cursor inline-block w-[2px] h-3 ml-0.5 align-middle"
                    style={{ background: "#D77F70" }}
                  />
                </div>
              </div>
            )}
            {error && (
              <div className="text-xs text-red-300 inline-flex items-start gap-1.5 px-2 py-1 rounded bg-red-500/10 border border-red-500/30">
                <AlertCircle size={12} className="shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}
          </div>

              {/* Input */}
              <div
                className="flex items-end gap-2 px-3 sm:px-6 py-3 border-t flex-shrink-0"
                style={{
                  borderTopColor: "rgba(139,58,46,0.20)",
                  background: "rgba(0,0,0,0.15)",
                }}
              >
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      send();
                    }
                  }}
                  placeholder="Pregúntame algo..."
                  rows={1}
                  disabled={loading}
                  className="flex-1 text-sm rounded-2xl bg-surface-hover border border-border px-4 py-2.5 resize-none disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-[#8B3A2E]/40 focus:border-[#8B3A2E]/60 transition"
                />
                <CaonaboVoiceButtonMaybe
                  onUserTranscript={handleVoiceUserTranscript}
                  onAssistantDelta={handleVoiceAssistantDelta}
                  onAssistantDone={handleVoiceAssistantDone}
                  onThinking={handleVoiceThinking}
                  onToolCall={handleVoiceToolCall}
                  onRichContent={handleVoiceRichContent}
                />
                <button
                  type="button"
                  onClick={() => send()}
                  disabled={loading || input.trim().length === 0}
                  aria-label="Enviar"
                  className="rounded-xl p-2.5 text-white disabled:opacity-40 transition-transform hover:scale-105 disabled:hover:scale-100"
                  style={{
                    background: "linear-gradient(135deg, #8B3A2E, #6B2820)",
                    boxShadow: "0 4px 10px rgba(139,58,46,0.35)",
                  }}
                >
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

function Message({
  message,
  onConfirmTool,
  onCancelTool,
}: {
  message: ChatMessage;
  onConfirmTool?: (toolName: string, pendingInput: Record<string, unknown>) => void;
  onCancelTool?: () => void;
}) {
  if (message.rol === "user") {
    return (
      <div className="flex justify-end">
        <div
          className="max-w-[85%] text-xs px-3 py-2 rounded-2xl rounded-br-sm text-white shadow-sm"
          style={{
            background: "linear-gradient(135deg, #8B3A2E 0%, #6B2820 100%)",
            boxShadow: "0 1px 3px rgba(139,58,46,0.30)",
          }}
        >
          {linkify(message.contenido)}
        </div>
      </div>
    );
  }
  if (message.rol === "tool") {
    // Defensa: si por alguna razón llega JSON crudo en contenido, mostramos
    // solo el nombre de la tool. Nunca exponemos el JSON al usuario final.
    const looksLikeJson =
      message.contenido?.trim().startsWith("{") || message.contenido?.trim().startsWith("[");
    const display =
      looksLikeJson || !message.contenido
        ? `✓ ${message.toolName ?? "tool"}`
        : message.contenido;
    return (
      <div className="flex justify-center">
        <div
          className="text-[10px] inline-flex items-center gap-1 px-2 py-0.5 rounded-full"
          style={{
            background: "rgba(139,58,46,0.08)",
            border: "1px solid rgba(139,58,46,0.20)",
            color: "#D77F70",
          }}
        >
          <Wrench size={9} /> {display}
        </div>
      </div>
    );
  }
  const richContent = "richContent" in message ? message.richContent : undefined;
  return (
    <div className="flex flex-col gap-1.5">
      {message.contenido && (
        <div className="flex justify-start">
          <div
            className="max-w-[90%] text-xs px-3 py-2 rounded-2xl rounded-bl-sm whitespace-pre-wrap text-text"
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            {linkify(message.contenido)}
          </div>
        </div>
      )}
      {richContent && richContent.length > 0 && (
        <div className="space-y-2">
          {dedupRichMaps(richContent).map((rc, i) => (
            <ChatRichContent
              key={i}
              content={rc}
              onConfirm={onConfirmTool}
              onCancel={onCancelTool}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Convierte URLs http(s):// y www.* en <a> clickeables. Devuelve un array
 * de nodos React mezclando texto y enlaces. Usado en mensajes user y assistant.
 */
const URL_REGEX = /(https?:\/\/[^\s<>"]+|www\.[^\s<>"]+)/gi;
function linkify(text: string): React.ReactNode {
  if (!text) return text;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  URL_REGEX.lastIndex = 0;
  while ((match = URL_REGEX.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    let url = match[0];
    // Quitar puntuación final que normalmente no es parte de la URL
    let trailing = "";
    while (/[.,;:!?)\]]$/.test(url)) {
      trailing = url.slice(-1) + trailing;
      url = url.slice(0, -1);
    }
    const href = url.startsWith("www.") ? `https://${url}` : url;
    parts.push(
      <a
        key={`l-${match.index}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="underline hover:opacity-80"
        style={{ color: "#D77F70" }}
      >
        {url}
      </a>
    );
    if (trailing) parts.push(trailing);
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts.length === 1 ? parts[0] : <>{parts}</>;
}
